import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { isEqual } from "lodash-es";
import { v7 as uuidv7 } from "uuid";
import { FIELD_TYPES, VALIDATIONS } from "@hyro-components/fields/utils";
import { BULK_EDIT_MODES } from "src/components/collections/bulk-actions/BulkActions.consts";
import { VALIDATIONS_SEVERITY, VALIDATION_TYPES } from "src/components/collections/consts";
import { capitalizeAndCleanUnderscore } from "@utils";
import {
  ASSOCIATION_COPY,
  CELL_ERROR_CLASS,
  CONFIG_REF_SCALAR_NAMES,
  GRAPHQL_KINDS,
  ID_FIELD_SUFFIX,
  KNOWLEDGE_SOURCES_VALIDATION_PREFIX,
  MANDATORY_FIELD_ERROR,
  REF_KINDS,
  REF_VERSION_CALL_MARKER,
  ROW_LEVEL_PATH_LEAF,
  SCALAR_NAMES,
  SERVER_ERROR_STATUS,
  SORT_INPUT_DIRECTIONS,
  SORTABLE_SCALAR_NAMES,
  UNIQUE_BY_ENTITY_ASSOCIATIONS,
  UNIQUE_VIOLATION_CODE
} from "./ExploreTab.consts";
import {
  EXACT_MATCH_OPERATORS,
  FILTER_MAP_KEYS,
  KNOWLEDGE_FILTER_OPERATORS,
  OPERATORS_BY_TYPE,
  getDefaultOperator
} from "./knowledgeFilterMap";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const VERSION_TIMESTAMP_FORMAT = "YYYYMMDDHHmmss";

export const unwrapNonNull = (type) => (type?.kind === GRAPHQL_KINDS.NON_NULL ? type.ofType : type);

export const isNullableField = (field) => field?.type?.kind !== GRAPHQL_KINDS.NON_NULL;

export const isJsonArrayOfObjects = (value) =>
  Array.isArray(value) && value.length > 0 && value.some((entry) => entry !== null && typeof entry === "object");

export const isJsonArrayOfPrimitives = (value) =>
  Array.isArray(value) && value.length > 0 && value.every((entry) => entry === null || typeof entry !== "object");

const ES_SUFFIX_ENDINGS = ["ses", "xes", "ches", "shes", "zes"];

export const singularizeLabel = (label) => {
  if (typeof label !== "string" || label.length < 2) return label;
  if (label.endsWith("ies") && label.length > 3) return `${label.slice(0, -3)}y`;
  if (ES_SUFFIX_ENDINGS.some((ending) => label.toLowerCase().endsWith(ending))) {
    return label.slice(0, -2);
  }
  if (label.endsWith("s")) return label.slice(0, -1);
  return label;
};

export const getListingsLabel = (field, value) => {
  const count = Array.isArray(value) ? value.length : value ? 1 : 0;
  if (count === 0) return null;
  const fieldLabel = capitalizeAndCleanUnderscore(field.name);
  return `${count} ${count === 1 ? singularizeLabel(fieldLabel) : fieldLabel}`;
};

const getFieldKindInfo = (field) => {
  const type = unwrapNonNull(field?.type);
  if (!type) return { kind: null, ofTypeKind: null, scalarName: null };
  const ofType = unwrapNonNull(type.ofType);
  return {
    kind: type.kind,
    ofTypeKind: ofType?.kind ?? null,
    scalarName: type.kind === GRAPHQL_KINDS.SCALAR ? type.name : null
  };
};

export const isBooleanField = (field) => {
  const { kind, scalarName } = getFieldKindInfo(field);
  return kind === GRAPHQL_KINDS.SCALAR && scalarName === SCALAR_NAMES.BOOLEAN;
};

export const isJsonField = (field) => {
  const { kind, scalarName } = getFieldKindInfo(field);
  return kind === GRAPHQL_KINDS.SCALAR && scalarName === SCALAR_NAMES.JSON;
};

const NUMERIC_SCALAR_TO_FIELD_TYPE = {
  [SCALAR_NAMES.INT]: FIELD_TYPES.INT,
  [SCALAR_NAMES.FLOAT]: FIELD_TYPES.FLOAT
};

const getListElementScalarName = (field) => {
  const type = unwrapNonNull(field?.type);
  if (type?.kind !== GRAPHQL_KINDS.LIST) return null;
  const element = unwrapNonNull(type.ofType);
  return element?.kind === GRAPHQL_KINDS.SCALAR ? element.name : null;
};

const getListElementNumericFieldType = (field) =>
  NUMERIC_SCALAR_TO_FIELD_TYPE[getListElementScalarName(field)] ?? null;

// The ENUM type of a bare enum field or of an enum list's element, else null.
const getEnumType = (field) => {
  const type = unwrapNonNull(field?.type);
  if (type?.kind === GRAPHQL_KINDS.ENUM) return type;
  if (type?.kind !== GRAPHQL_KINDS.LIST) return null;
  const element = unwrapNonNull(type.ofType);
  return element?.kind === GRAPHQL_KINDS.ENUM ? element : null;
};

// RESCHEDULE_APPOINTMENT -> "Reschedule appointment" (capitalizeAndCleanUnderscore alone keeps the caps).
const toEnumDisplayName = (name) => capitalizeAndCleanUnderscore(String(name).toLowerCase());

// Select options in the shape HyroMultiSelect / HyroSingleSelect consume. The value sent
// to the API is the enum name (key); the label prefers the enum value's description.
export const getEnumOptions = (field) =>
  (getEnumType(field)?.enumValues ?? []).map(({ name, description }) => ({
    key: name,
    display_key: description ?? toEnumDisplayName(name)
  }));

const toOptionKey = (entry) => (entry && typeof entry === "object" ? entry.key : entry);

// Labels for stored enum names, matching what the selects display for the same options.
export const getEnumLabels = (field, value) => {
  if (!Array.isArray(value)) return [];
  const options = getEnumOptions(field);
  return value.map((entry) => {
    const key = toOptionKey(entry);
    const option = options.find((candidate) => candidate.key === key);
    return option ? capitalizeAndCleanUnderscore(option.display_key) : toEnumDisplayName(key);
  });
};

// Label for a single stored enum name; null when unset so the cell shows the empty placeholder.
export const getEnumLabel = (field, value) => {
  const key = toOptionKey(value);
  if (key === null || key === undefined || key === "") return null;
  return getEnumLabels(field, [key])[0];
};

export const mapFieldToFieldType = (field, runtimeValue) => {
  const { kind, ofTypeKind, scalarName } = getFieldKindInfo(field);

  if (kind === GRAPHQL_KINDS.SCALAR) {
    if (scalarName === SCALAR_NAMES.BOOLEAN) return FIELD_TYPES.BOOL;
    if (scalarName === SCALAR_NAMES.INT) return FIELD_TYPES.INT;
    if (scalarName === SCALAR_NAMES.FLOAT) return FIELD_TYPES.FLOAT;
    if (scalarName === SCALAR_NAMES.MULTI_LANGUAGE_STRING) return FIELD_TYPES.MULTI_LANGUAGE_TEXT;
    if (scalarName === SCALAR_NAMES.ACTION) return FIELD_TYPES.ACTION;
    if (scalarName === SCALAR_NAMES.JSON) {
      if (isJsonArrayOfObjects(runtimeValue)) return FIELD_TYPES.OBJECT;
      if (isJsonArrayOfPrimitives(runtimeValue)) return FIELD_TYPES.LIST;
      return FIELD_TYPES.TEXT;
    }
    return FIELD_TYPES.TEXT;
  }

  if (kind === GRAPHQL_KINDS.LIST) {
    if (ofTypeKind === GRAPHQL_KINDS.OBJECT) return FIELD_TYPES.OBJECT;
    if (ofTypeKind === GRAPHQL_KINDS.ENUM) return FIELD_TYPES.CHOOSE_AT_LEAST_ONE;
    return FIELD_TYPES.LIST;
  }

  if (kind === GRAPHQL_KINDS.OBJECT) return FIELD_TYPES.OBJECT;
  if (kind === GRAPHQL_KINDS.ENUM) return FIELD_TYPES.CHOOSE_ONE;

  return FIELD_TYPES.TEXT;
};

const SCALAR_NAME_TO_MAP_KEY = {
  [SCALAR_NAMES.BOOLEAN]: FILTER_MAP_KEYS.BOOLEAN,
  [SCALAR_NAMES.INT]: FILTER_MAP_KEYS.INT,
  [SCALAR_NAMES.FLOAT]: FILTER_MAP_KEYS.FLOAT,
  [SCALAR_NAMES.JSON]: FILTER_MAP_KEYS.JSON
};

// Classifies an introspected field into a knowledgeFilterMap key + nullability,
// using type info alone (no runtime value): [String!] -> "[String]", JSON scalar ->
// "JSON", String/Int/Float/Boolean -> their key, ENUM -> "String". Returns null for
// fields with no filter map entry (object arrays / relationships) so callers skip them.
export const getColumnFilterType = (field) => {
  const topType = field?.type;
  if (!topType) return null;
  const nullable = topType.kind !== GRAPHQL_KINDS.NON_NULL;
  const inner = unwrapNonNull(topType);
  if (!inner) return null;

  if (inner.kind === GRAPHQL_KINDS.SCALAR) {
    if (inner.name === SCALAR_NAMES.ACTION) return null;
    if (inner.name === SCALAR_NAMES.MULTI_LANGUAGE_STRING) {
      return { mapKey: FILTER_MAP_KEYS.MULTI_LANGUAGE, nullable };
    }
    return { mapKey: SCALAR_NAME_TO_MAP_KEY[inner.name] ?? FILTER_MAP_KEYS.STRING, nullable };
  }
  if (inner.kind === GRAPHQL_KINDS.ENUM) {
    return { mapKey: FILTER_MAP_KEYS.STRING, nullable };
  }
  if (inner.kind === GRAPHQL_KINDS.LIST) {
    const element = unwrapNonNull(inner.ofType);
    if (element?.kind === GRAPHQL_KINDS.SCALAR && element.name === SCALAR_NAMES.STRING) {
      return { mapKey: FILTER_MAP_KEYS.STRING_ARRAY, nullable };
    }
    return null;
  }
  return null;
};

export const buildOperatorOptions = (mapKey) =>
  (OPERATORS_BY_TYPE[mapKey] ?? []).map((op) => ({ key: op, display_key: KNOWLEDGE_FILTER_OPERATORS[op].label }));

export const getRowIdValue = (row, fields) => {
  if (!Array.isArray(fields) || !row) return null;
  const idField = fields.find(isIdField);
  return idField ? row[idField.name] ?? null : null;
};

export const parseVersionTimestamp = (version) => {
  if (typeof version !== "string") return null;
  const parsed = dayjs.utc(version, VERSION_TIMESTAMP_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

const getKnowledgeMapKey = (spec) => spec?.group ?? spec?.schema_type ?? null;

export const getKnowledgeVersion = (knowledge, spec) => {
  const key = getKnowledgeMapKey(spec);
  if (!key) return null;
  const version = knowledge?.[key];
  return parseVersionTimestamp(version) ? version : null;
};

export const getLatestVersion = (versions) => {
  if (!Array.isArray(versions) || versions.length === 0) return null;
  const sorted = [...versions]
    .filter((v) => parseVersionTimestamp(v))
    .sort((a, b) => parseVersionTimestamp(b).valueOf() - parseVersionTimestamp(a).valueOf());
  return sorted[0] ?? versions[0] ?? null;
};

// The pk is a fact when the fields were annotated from the update-input
// introspection (annotatePkField); the suffix heuristic is only the fallback
// for schemas without an update mutation. It requires NON_NULL because every
// real pk is non-null, while *_id data columns (transfer_policy_id, ehr_id,
// external_route_id) are nullable and must stay editable.
export const isIdField = (field) => {
  if (typeof field?.isPk === "boolean") return field.isPk;
  return (
    typeof field?.name === "string"
    && field.name.toLowerCase().endsWith(ID_FIELD_SUFFIX)
    && field?.type?.kind === GRAPHQL_KINDS.NON_NULL
    && isBuiltInScalarType(field.type)
  );
};

// The generated Update<Type>Input carries every editable column but not the pk
// (it travels as the separate `id` argument), so the single id-like field
// missing from it is the pk. Anything ambiguous falls back to the heuristic.
export const getPkFieldName = (fields, updateInputFields) => {
  if (!Array.isArray(fields) || !Array.isArray(updateInputFields)) return null;
  const inputNames = new Set(updateInputFields.map((field) => field?.name));
  const candidates = fields.filter(
    (field) =>
      typeof field?.name === "string"
      && field.name.toLowerCase().endsWith(ID_FIELD_SUFFIX)
      && isBuiltInScalarType(field.type)
      && !inputNames.has(field.name)
  );
  return candidates.length === 1 ? candidates[0].name : null;
};

export const annotatePkField = (fields, pkFieldName) => {
  if (!Array.isArray(fields) || !pkFieldName) return fields;
  return fields.map((field) => ({ ...field, isPk: field.name === pkFieldName }));
};

export const annotateRequiredInputFields = (fields, createInputFields) => {
  if (!Array.isArray(fields) || !Array.isArray(createInputFields)) return fields;
  const requiredNames = new Set(
    createInputFields.filter((field) => field?.type?.kind === GRAPHQL_KINDS.NON_NULL).map((field) => field.name)
  );
  return fields.map((field) => ({ ...field, isRequiredInput: requiredNames.has(field.name) }));
};

export const isEditableFieldType = (field, runtimeValue) =>
  mapFieldToFieldType(field, runtimeValue) !== FIELD_TYPES.OBJECT;

export const isInlineEditableFieldType = (field, runtimeValue) => {
  const fieldType = mapFieldToFieldType(field, runtimeValue);
  return fieldType !== FIELD_TYPES.OBJECT && fieldType !== FIELD_TYPES.ACTION;
};

export const isInlineEditableField = (field, runtimeValue) =>
  isInlineEditableFieldType(field, runtimeValue) && !isIdField(field);

export const getRequiredFieldNames = (fields) =>
  (fields ?? [])
    .filter((field) => field?.type?.kind === GRAPHQL_KINDS.NON_NULL && !isIdField(field) && !isAssociationField(field))
    .map((field) => field.name);

export const withGeneratedId = (input, fields) => {
  const idField = (fields ?? []).find(isIdField);
  if (!idField) return input;
  const current = input?.[idField.name];
  if (current !== undefined && current !== null && current !== "") return input;
  return { ...input, [idField.name]: uuidv7() };
};

const getEmptyValueForField = (field) => {
  const fieldType = mapFieldToFieldType(field);
  if (fieldType === FIELD_TYPES.BOOL) return undefined;
  if (fieldType === FIELD_TYPES.LIST || fieldType === FIELD_TYPES.CHOOSE_AT_LEAST_ONE) return [];
  if (fieldType === FIELD_TYPES.MULTI_LANGUAGE_TEXT || fieldType === FIELD_TYPES.ACTION) return {};
  return "";
};

export const withAssociationUiKeys = (field, value) =>
  (Array.isArray(value) ? value : []).map((item, index) => ({ ...item, __uiKey: `${field.name}-${index}` }));

export const buildDrawerInitialValues = (fields, row, mode) =>
  (fields ?? [])
    .filter((field) => !isIdField(field))
    .filter((field) => isEditableFieldType(field, row?.[field.name]) || isAssociationField(field))
    .reduce((acc, field) => {
      if (isAssociationField(field)) {
        // Stamp a stable client-only key per row so association cards keep their identity
        // across reorders/deletes; coerceAssociationValue strips __uiKey before the mutation.
        acc[field.name] = withAssociationUiKeys(field, row?.[field.name]);
      } else {
        acc[field.name] = mode === "create" ? (row?.[field.name] ?? getEmptyValueForField(field)) : row?.[field.name];
      }
      return acc;
    }, {});

export const areRequiredFieldsFilled = (values, requiredNames, fields) =>
  (requiredNames ?? []).every((name) => {
    const value = values?.[name];
    const field = (fields ?? []).find((f) => f.name === name);
    if (field) return !isEmptyFieldValue(field, value);
    if (Array.isArray(value)) return value.length > 0;
    return value !== "" && value !== null && value !== undefined;
  });

const NUMERIC_FIELD_TYPES = [FIELD_TYPES.INT, FIELD_TYPES.FLOAT];

// Don't silently turn unparseable input ("A") into NaN → null. Keep the raw value so
// an invalid entry that slipped past validation fails loudly instead of vanishing.
export const coerceNumeric = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? value : num;
};

export const coerceFieldValue = (field, value) => {
  if (isAssociationField(field)) return coerceAssociationValue(field, value);
  const fieldType = mapFieldToFieldType(field);
  if (NUMERIC_FIELD_TYPES.includes(fieldType)) return coerceNumeric(value);
  // The selects emit option objects; the API takes the enum names. An empty single
  // select is null rather than "", which the enum would reject.
  if (fieldType === FIELD_TYPES.CHOOSE_AT_LEAST_ONE) return Array.isArray(value) ? value.map(toOptionKey) : value;
  if (fieldType === FIELD_TYPES.CHOOSE_ONE) {
    const key = toOptionKey(value);
    return key === "" ? null : key;
  }
  if (fieldType === FIELD_TYPES.LIST && getListElementNumericFieldType(field)) {
    if (!Array.isArray(value)) return value;
    return value.map(coerceNumeric).filter((entry) => entry !== null);
  }
  return value;
};

const isEmptyEntry = (entry) => entry === "" || entry === null || entry === undefined;

export const normalizeLanguageMap = (value, collectionLanguages) =>
  (collectionLanguages ?? []).reduce((acc, lang) => {
    acc[lang] = value?.[lang] ?? "";
    return acc;
  }, {});

export const isEmptyFieldValue = (field, value) => {
  const fieldType = mapFieldToFieldType(field, value);
  if (fieldType === FIELD_TYPES.MULTI_LANGUAGE_TEXT) {
    return !Object.values(value ?? {}).some((text) => !isEmptyEntry(text));
  }
  if (fieldType === FIELD_TYPES.ACTION) {
    return !Object.values(value ?? {}).some((entry) => !isEmptyEntry(entry?.text));
  }
  if (Array.isArray(value)) return value.length === 0;
  return isEmptyEntry(value);
};

export const isEmptyRow = (row, fields) =>
  (fields ?? [])
    .filter((field) => !isIdField(field))
    .every((field) => {
      const value = row?.[field.name];
      if (mapFieldToFieldType(field, value) === FIELD_TYPES.BOOL) return value !== true;
      return isEmptyFieldValue(field, value);
    });

export const coerceAssociationValue = (field, value) => {
  if (!Array.isArray(value)) return [];
  const subFields = getAssociationSubFields(field);
  const entityKeys = subFields.filter((s) => s.entityType).map((s) => s.name);
  return value
    .filter((item) => item && (entityKeys.length === 0 || entityKeys.some((k) => !isEmptyEntry(item[k]))))
    .map((item) =>
      subFields.reduce((acc, s) => {
        acc[s.name] = item[s.name] ?? (s.fieldType === FIELD_TYPES.LIST ? [] : "");
        return acc;
      }, {})
    );
};

// An association item without its entity reference cannot be persisted (the FK is the
// row's identity), so it must block submit — coerceAssociationValue would otherwise
// silently drop it along with any sub-field values the user did fill in.
export const getAssociationItemError = (field, item) => {
  const hasEmptyEntity = getAssociationSubFields(field)
    .filter((subField) => subField.entityType)
    .some((subField) => isEmptyEntry(item?.[subField.name]));
  return hasEmptyEntity ? MANDATORY_FIELD_ERROR : undefined;
};

export const getAssociationEntitySubField = (field) => getAssociationSubFields(field).find((subField) => subField.entityType) ?? null;

export const isAssociationLinkField = (field, pkFieldName) => {
  const editableSubFields = getAssociationSubFields(field).filter((subField) => subField.name !== pkFieldName);
  return editableSubFields.length === 1 && Boolean(editableSubFields[0].entityType);
};

export const isUniqueByEntityAssociation = (field) => UNIQUE_BY_ENTITY_ASSOCIATIONS.has(getAssociationSchemaType(field));

export const getAssociationDuplicateIndexes = (field, items) => {
  const duplicates = new Set();
  if (!isUniqueByEntityAssociation(field) || !Array.isArray(items)) return duplicates;
  const entityKey = getAssociationEntitySubField(field)?.name;
  const seen = new Set();
  items.forEach((item, index) => {
    const entityId = item?.[entityKey];
    if (isEmptyEntry(entityId)) return;
    if (seen.has(entityId)) duplicates.add(index);
    seen.add(entityId);
  });
  return duplicates;
};

export const getAssociationDuplicateError = (field) => {
  const entityType = getAssociationEntitySubField(field)?.entityType;
  const itemLabel = singularizeLabel(capitalizeAndCleanUnderscore(field.name)).toLowerCase();
  return ASSOCIATION_COPY.duplicateEntity(capitalizeAndCleanUnderscore(entityType), itemLabel);
};

export const buildAssociationLinkItems = (field, previousItems, selectedIds, pkFieldName) => {
  const entityKey = getAssociationEntitySubField(field)?.name;
  const previousById = new Map((previousItems ?? []).map((item) => [item?.[entityKey], item]));
  return selectedIds.map((id) =>
    previousById.get(id) ?? { ...(pkFieldName ? { [pkFieldName]: uuidv7() } : {}), [entityKey]: id }
  );
};

export const getAssociationLinkIds = (field, items) => {
  const entityKey = getAssociationEntitySubField(field)?.name;
  return [...new Set((Array.isArray(items) ? items : []).map((item) => item?.[entityKey]).filter((id) => !isEmptyEntry(id)))];
};

const getUniqueFieldLabel = (fieldName, fields) => {
  const field = (fields ?? []).find((f) => f.name === fieldName);
  const entityType = getFieldRefInfo(field)?.entityType;
  return capitalizeAndCleanUnderscore(entityType ?? fieldName.slice(0, fieldName.endsWith(ID_FIELD_SUFFIX) ? -ID_FIELD_SUFFIX.length : undefined));
};

export const getUniqueViolationError = ({ error, fields, schemaType, editedFieldNames }) => {
  const violation = error?.response?.errors?.find((gqlError) => gqlError?.extensions?.code === UNIQUE_VIOLATION_CODE);
  const { fields: violationFields, schema_type: violationSchemaType } = violation?.extensions ?? {};
  if (!Array.isArray(violationFields) || violationFields.length === 0) return null;
  const edited = violationFields.filter((name) => Array.isArray(editedFieldNames) && editedFieldNames.includes(name));
  const [first, ...rest] = (edited.length > 0 ? edited : violationFields).map((name) => getUniqueFieldLabel(name, fields));
  const entityLabel = [first, ...rest.map((label) => label.toLowerCase())].join(" and ");
  return ASSOCIATION_COPY.duplicateEntity(entityLabel, capitalizeAndCleanUnderscore(violationSchemaType ?? schemaType).toLowerCase());
};

const getAssociationFieldError = (field, value) => {
  const hasIncompleteItem = (Array.isArray(value) ? value : []).some((item) => getAssociationItemError(field, item));
  if (hasIncompleteItem) return MANDATORY_FIELD_ERROR;
  return getAssociationDuplicateIndexes(field, value).size > 0 ? getAssociationDuplicateError(field) : undefined;
};

export const getFieldFormatError = (field, value) => {
  if (isAssociationField(field)) return getAssociationFieldError(field, value);

  const fieldType = mapFieldToFieldType(field, value);

  if (fieldType === FIELD_TYPES.LIST) {
    const elementFieldType =
      getListElementNumericFieldType(field) ??
      (getListElementScalarName(field) === SCALAR_NAMES.STRING ? FIELD_TYPES.TEXT : null);
    const validation = VALIDATIONS[elementFieldType];
    if (!validation?.TEST || !Array.isArray(value)) return undefined;
    const hasInvalid = value.some((entry) => !isEmptyEntry(entry) && !validation.TEST.test(String(entry)));
    return hasInvalid ? validation.MESSAGE : undefined;
  }

  if (fieldType === FIELD_TYPES.MULTI_LANGUAGE_TEXT) {
    const validation = VALIDATIONS[FIELD_TYPES.MULTI_LANGUAGE_TEXT];
    const hasInvalid = Object.values(value ?? {}).some(
      (text) => !isEmptyEntry(text) && !validation.TEST.test(String(text))
    );
    return hasInvalid ? validation.MESSAGE : undefined;
  }

  const validation = VALIDATIONS[fieldType];
  if (!validation?.TEST || isEmptyEntry(value)) return undefined;
  return validation.TEST.test(String(value)) ? undefined : validation.MESSAGE;
};

export const isMissingRequiredValue = (field, value, requiredFieldNames) =>
  (requiredFieldNames ?? []).includes(field.name) && isEmptyFieldValue(field, value);

export const getCellEditError = (field, value, requiredFieldNames) => {
  if (isMissingRequiredValue(field, value, requiredFieldNames)) return MANDATORY_FIELD_ERROR;
  return getFieldFormatError(field, value);
};

export const getFieldErrors = (values, fields) =>
  (fields ?? []).reduce((acc, field) => {
    const error = getFieldFormatError(field, values?.[field.name]);
    if (error) acc[field.name] = error;
    return acc;
  }, {});

// Treats empty string / null / undefined / empty array as the same "empty" so a
// field that was absent on the row and seeded to "" / [] doesn't count as a change.
const normalizeForDiff = (field, value) => {
  const coerced = coerceFieldValue(field, value);
  if (coerced === "" || coerced === null || coerced === undefined) return null;
  if (Array.isArray(coerced) && coerced.length === 0) return null;
  return coerced;
};

export const buildMutationInput = (values, baseline, fields, mode) => {
  const allFields = fields ?? [];
  const editableFields = allFields
    .filter((field) => !isIdField(field))
    .filter((field) => isEditableFieldType(field, baseline?.[field.name]));
  const associationFields = allFields.filter(isAssociationField);

  if (mode === "create") {
    const acc = editableFields.reduce((inner, field) => {
      const value = coerceFieldValue(field, values[field.name]);
      if (value !== undefined) inner[field.name] = value;
      return inner;
    }, {});
    associationFields.forEach((field) => {
      acc[field.name] = coerceAssociationValue(field, values[field.name]);
    });
    return acc;
  }

  const acc = editableFields
    .filter((field) => !isEqual(normalizeForDiff(field, values[field.name]), normalizeForDiff(field, baseline?.[field.name])))
    .reduce((inner, field) => {
      inner[field.name] = coerceFieldValue(field, values[field.name]);
      return inner;
    }, {});

  associationFields.forEach((field) => {
    const next = coerceAssociationValue(field, values[field.name]);
    const prev = coerceAssociationValue(field, baseline?.[field.name]);
    if (!isEqual(next, prev)) acc[field.name] = next;
  });
  return acc;
};

// The only field types whose empty value survives `buildMutationInput` as a non-null argument:
// text -> "", lists -> [], language maps / actions -> {}. A bool has no empty value (undefined is
// dropped), and "" coerces to null for numbers / enums — so a required one can't be seeded.
const INLINE_SEEDABLE_FIELD_TYPES = new Set([
  FIELD_TYPES.TEXT,
  FIELD_TYPES.LIST,
  FIELD_TYPES.CHOOSE_AT_LEAST_ONE,
  FIELD_TYPES.MULTI_LANGUAGE_TEXT,
  FIELD_TYPES.ACTION
]);

// Inline "+ Add New" persists the row at once: the pk is generated and every required field is
// seeded with its type's empty value, satisfying the non-null arguments of `Create<Type>Input`.
// Returns null when a required field has no seedable empty value (see INLINE_SEEDABLE_FIELD_TYPES),
// so the caller can fall back to the create drawer instead of sending a create the service rejects.
export const buildInlineCreateInput = (fields) => {
  const requiredFieldNames = getRequiredFieldNames(fields);
  const requiredFields = (fields ?? []).filter((field) => requiredFieldNames.includes(field.name));
  if (requiredFields.some((field) => !INLINE_SEEDABLE_FIELD_TYPES.has(mapFieldToFieldType(field)))) return null;
  const values = requiredFields.reduce((acc, field) => {
    acc[field.name] = getEmptyValueForField(field);
    return acc;
  }, {});
  return withGeneratedId(buildMutationInput(values, null, fields, "create"), fields);
};

export const buildDuplicateCreateInput = (row, fields) => {
  const input = (fields ?? [])
    .filter((field) => !isIdField(field) && !isAssociationField(field))
    .reduce((acc, field) => {
      if (row?.[field.name] !== undefined) acc[field.name] = row[field.name];
      return acc;
    }, {});
  return withGeneratedId(input, fields);
};

const getObjectType = (type) => {
  let current = type;
  while (current) {
    if (current.kind === GRAPHQL_KINDS.OBJECT && Array.isArray(current.fields) && current.fields.length) {
      return current;
    }
    current = current.ofType;
  }
  return null;
};

const BUILT_IN_SCALAR_NAMES = new Set(Object.values(SCALAR_NAMES));

const isBuiltInScalarType = (type) => {
  const inner = unwrapNonNull(type);
  return inner?.kind === GRAPHQL_KINDS.SCALAR && BUILT_IN_SCALAR_NAMES.has(inner.name);
};

// Implicit backend contract: a custom scalar's `description` is the associable entity's
// schema type name (e.g. LocationId -> "location"), used verbatim to introspect and query
// that entity. A non-built-in scalar whose description is not a queryable type would break.
export const getScalarDescription = (type) => {
  const inner = unwrapNonNull(type);
  if (inner?.kind !== GRAPHQL_KINDS.SCALAR) return null;
  if (BUILT_IN_SCALAR_NAMES.has(inner.name)) return null;
  return inner.description ?? null;
};

const CONFIG_REF_DESCRIPTION_PATTERN = /^[\w/-]+(\?\S*)?$/;

const computeFieldRefInfo = (field) => {
  const inner = unwrapNonNull(field?.type);
  if (inner?.kind !== GRAPHQL_KINDS.SCALAR || BUILT_IN_SCALAR_NAMES.has(inner.name)) return null;

  const description = field?.description;
  if (typeof description !== "string" || description.trim() === "") return null;

  if (CONFIG_REF_SCALAR_NAMES.has(inner.name)) {
    if (!CONFIG_REF_DESCRIPTION_PATTERN.test(description)) return null;
    const queryIndex = description.indexOf("?");
    const collectionId = queryIndex === -1 ? description : description.slice(0, queryIndex);
    const queryString = queryIndex === -1 ? "" : description.slice(queryIndex + 1);
    const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};
    return { kind: REF_KINDS.CONFIG, scalarName: inner.name, collectionId, queryParams };
  }

  if (!inner.description || !description.includes(REF_VERSION_CALL_MARKER)) return null;
  const selectionMatch = description.match(/\{([^}]*)\}/);
  const selectedFieldNames = (selectionMatch?.[1] ?? "").trim().split(/\s+/).filter(Boolean);
  const [valueField, labelField = valueField] = selectedFieldNames;
  if (!valueField) return null;
  return { kind: REF_KINDS.ENTITY, entityType: inner.description, valueField, labelField };
};

const fieldRefInfoCache = new WeakMap();
export const getFieldRefInfo = (field) => {
  if (field === null || typeof field !== "object") return null;
  if (!fieldRefInfoCache.has(field)) fieldRefInfoCache.set(field, computeFieldRefInfo(field));
  return fieldRefInfoCache.get(field);
};

export const isRefField = (field) => Boolean(getFieldRefInfo(field));

const isSortableListElement = (element) =>
  (element?.kind === GRAPHQL_KINDS.SCALAR && element.name === SCALAR_NAMES.STRING) ||
  element?.kind === GRAPHQL_KINDS.ENUM;

export const isSortableField = (field) => {
  const refInfo = getFieldRefInfo(field);
  if (refInfo) return refInfo.kind === REF_KINDS.ENTITY;
  const inner = unwrapNonNull(field?.type);
  if (!inner) return false;
  if (inner.kind === GRAPHQL_KINDS.SCALAR) {
    return SORTABLE_SCALAR_NAMES.has(inner.name);
  }
  if (inner.kind === GRAPHQL_KINDS.ENUM) return true;
  if (inner.kind === GRAPHQL_KINDS.LIST) return isSortableListElement(unwrapNonNull(inner.ofType));
  return false;
};

export const toSortInput = (sortModel, language) => {
  const [item] = sortModel ?? [];
  if (!item?.sort) return null;
  return { field: item.field, direction: SORT_INPUT_DIRECTIONS[item.sort], language: language ?? null };
};

export const isGraphQLRejection = (error) =>
  Boolean(error?.response?.errors?.length) && (error.response.status ?? 0) < SERVER_ERROR_STATUS;

export const isAssociationField = (field) => {
  const type = unwrapNonNull(field?.type);
  if (type?.kind !== GRAPHQL_KINDS.LIST) return false;
  if (!getObjectType(unwrapNonNull(type.ofType))) return false;
  return getAssociationSubFields(field).some((subField) => subField.entityType);
};

const getAssociationObjectType = (field) => {
  const type = unwrapNonNull(field?.type);
  return getObjectType(unwrapNonNull(type?.ofType));
};

export const getAssociationSubFields = (field) => {
  const objectType = getAssociationObjectType(field);
  if (!objectType) return [];
  return objectType.fields.map((subField) => ({
    name: subField.name,
    type: subField.type,
    fieldType: mapFieldToFieldType(subField),
    entityType: getScalarDescription(subField.type)
  }));
};

// The server names an association's row type PascalCase(its schema type) + "From<Source>" —
// "OutcomeFromSite" -> "outcome", "SiteSiteAssociationFromSite" -> "site_site_association" — so the
// association's own schema type (and thus its Update<Type>Input) is recoverable from the name.
const ASSOCIATION_SOURCE_SEPARATOR = "From";

const stripAssociationSource = (typeName) => {
  const separatorIndex = typeName.lastIndexOf(ASSOCIATION_SOURCE_SEPARATOR);
  const sourceStart = typeName[separatorIndex + ASSOCIATION_SOURCE_SEPARATOR.length];
  const isSourceSuffix = separatorIndex > 0
    && /[a-z0-9]/.test(typeName[separatorIndex - 1])
    && Boolean(sourceStart) && /[A-Z]/.test(sourceStart);
  return isSourceSuffix ? typeName.slice(0, separatorIndex) : typeName;
};

export const getAssociationSchemaType = (field) => {
  const objectType = getAssociationObjectType(field);
  if (typeof objectType?.name !== "string" || objectType.name === "") return null;
  return stripAssociationSource(objectType.name)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();
};

// Same contract as the top-level row (useExploreSchema): the association's Update<Type>Input
// omits its pk. Null for mapping tables, which have no update mutation.
export const getAssociationPkFieldName = (field, updateInputFields) => {
  const objectType = getAssociationObjectType(field);
  return objectType ? getPkFieldName(objectType.fields, updateInputFields) : null;
};

// Validation paths arrive flattened as "knowledge-sources/{specKey}/{ksInstanceId}/{instanceId}/{columnName}",
// where ksInstanceId is the knowledge-source configurable instance uuid (ignored beyond a presence check)
// and columnName is the SQL column name from the GQL schema (matches introspected field.name).
// Regrouped by row instance so cells and the drawer can look up errors in O(1).
// WARNING-severity results are dropped entirely for now - only ERROR entries are displayed.
const isErrorSeverity = (error) => error?.error_severity_level === VALIDATIONS_SEVERITY.ERROR;

export const buildExploreValidationErrors = (validationResults, specKey) => {
  if (!specKey) return {};
  const prefix = `${KNOWLEDGE_SOURCES_VALIDATION_PREFIX}/${specKey}/`;
  return Object.entries(validationResults ?? {}).reduce((acc, [path, errors]) => {
    if (!path.startsWith(prefix)) return acc;
    const [ksInstanceId, instanceId, columnName, ...rest] = path.slice(prefix.length).split("/");
    if (!ksInstanceId || !instanceId || !columnName || rest.length > 0) return acc;
    const errorList = (Array.isArray(errors) ? errors : [errors]).filter(isErrorSeverity);
    if (errorList.length === 0) return acc;
    acc[instanceId] = { ...acc[instanceId], [columnName]: errorList };
    return acc;
  }, {});
};

// Invalid needs a BAD_CONFIGURATION at ERROR (as the knowledge cards count it); Unassigned is any
// UNUSED_CONFIGURATION, whatever its severity.
export const buildStatusPks = (scopeState, validationResults, specKey) => {
  const pksByType = { [VALIDATION_TYPES.BAD_CONFIGURATION]: new Set(), [VALIDATION_TYPES.UNASSIGNED]: new Set() };
  const add = (pk, error) => {
    if (!pk) return;
    if (error?.type === VALIDATION_TYPES.BAD_CONFIGURATION && !isErrorSeverity(error)) return;
    pksByType[error?.type]?.add(pk);
  };
  Object.entries(scopeState?.rows ?? {}).forEach(([pk, errors]) => (errors ?? []).forEach((error) => add(pk, error)));
  (scopeState?.type ?? []).forEach((error) => {
    const [pk, leaf] = (error?.path ?? "").split("/");
    if (leaf) add(pk, error);
  });
  if (specKey) {
    const prefix = `${KNOWLEDGE_SOURCES_VALIDATION_PREFIX}/${specKey}/`;
    Object.entries(validationResults ?? {}).forEach(([path, errors]) => {
      if (!path.startsWith(prefix)) return;
      const [ksInstanceId, instanceId, columnName, ...rest] = path.slice(prefix.length).split("/");
      if (!ksInstanceId || !instanceId || !columnName || rest.length > 0) return;
      (Array.isArray(errors) ? errors : [errors]).forEach((error) => add(instanceId, error));
    });
  }
  return {
    invalidPks: [...pksByType[VALIDATION_TYPES.BAD_CONFIGURATION]].sort(),
    unassignedPks: [...pksByType[VALIDATION_TYPES.UNASSIGNED]].sort()
  };
};

export const getValidationErrorMessage = (errors) =>
  (errors ?? []).map((error) => error?.details).filter(Boolean).join("\n") || undefined;

export const hasExploreValidationErrors = (validationResults, specKey) =>
  Object.values(buildExploreValidationErrors(validationResults, specKey)).some((columns) =>
    Object.values(columns).some((errors) => errors.length > 0)
  );

export const buildKnowledgeScopeErrors = (scopeState, fieldNames) => {
  const cells = {};
  const rowLevel = {};
  const add = (pk, column, error) => {
    if (!isErrorSeverity(error)) return;
    if (!column || column === ROW_LEVEL_PATH_LEAF || !fieldNames?.has(column)) {
      rowLevel[pk] = rowLevel[pk] ?? [];
      rowLevel[pk].push(error);
      return;
    }
    cells[pk] = cells[pk] ?? {};
    cells[pk][column] = cells[pk][column] ?? [];
    cells[pk][column].push(error);
  };
  Object.entries(scopeState?.rows ?? {}).forEach(([pk, errors]) =>
    (errors ?? []).forEach((error) => add(pk, (error.path ?? "").split("/")[1], error)));
  (scopeState?.type ?? []).forEach((error) => {
    const [pk, leaf] = (error.path ?? "").split("/");
    if (pk) add(pk, leaf, error);
  });
  return { cells, rowLevel };
};

export const getErroredRowIds = (validationView) =>
  [...new Set([...Object.keys(validationView?.cells ?? {}), ...Object.keys(validationView?.rowLevel ?? {})])]
    .sort();

export const mergeValidationViews = (mechanismOneCells, knowledgeView) => {
  const cells = { ...knowledgeView.cells };
  Object.entries(mechanismOneCells ?? {}).forEach(([pk, columns]) => {
    cells[pk] = { ...(cells[pk] ?? {}) };
    Object.entries(columns).forEach(([column, errors]) => {
      cells[pk][column] = [...errors, ...(cells[pk][column] ?? [])];
    });
  });
  return { cells, rowLevel: knowledgeView.rowLevel };
};

export const getCellErrorClass = (errors) => (errors?.length ? CELL_ERROR_CLASS : "");

export const summarizeBulkResults = (results) =>
  results.reduce(
    (acc, result) =>
      result.status === "fulfilled"
        ? { ...acc, ok: acc.ok + 1 }
        : { ...acc, failed: acc.failed + 1 },
    { ok: 0, failed: 0 }
  );

export const applyBulkEditMode = (currentValue, newValue, mode) => {
  if (!mode || mode === BULK_EDIT_MODES.REPLACE) return newValue;
  const inputText = String(newValue || "");
  if (Array.isArray(currentValue)) {
    if (mode === BULK_EDIT_MODES.PREFIX) return currentValue.map((item) => inputText + String(item || ""));
    if (mode === BULK_EDIT_MODES.SUFFIX) return currentValue.map((item) => String(item || "") + inputText);
    return newValue;
  }
  const currentText = String(currentValue || "");
  if (mode === BULK_EDIT_MODES.PREFIX) return inputText + currentText;
  if (mode === BULK_EDIT_MODES.SUFFIX) return currentText + inputText;
  return newValue;
};

export const toRefSelection = (value) => {
  if (Array.isArray(value)) return value;
  return value?.key ? [value] : [];
};

export const getFilledLanguages = (languageMap) =>
  Object.entries(languageMap ?? {}).reduce((acc, [lang, text]) => {
    const trimmed = typeof text === "string" ? text.trim() : text;
    return isEmptyEntry(trimmed) ? acc : { ...acc, [lang]: trimmed };
  }, {});

export const mergeBulkLanguageMap = (currentValue, inputValue) => ({
  ...(currentValue ?? {}),
  ...getFilledLanguages(inputValue)
});

export const buildEntityFilterSpec = ({ key, field, entityType, nullable, valueField, labelField, label }) => ({
  key,
  field,
  label: label ?? capitalizeAndCleanUnderscore(entityType),
  isActive: false,
  activeSince: null,
  value: null,
  mapKey: FILTER_MAP_KEYS.ASSOCIATION,
  entityType,
  valueField,
  labelField,
  nullable,
  operator: getDefaultOperator(FILTER_MAP_KEYS.ASSOCIATION),
  operatorOptions: buildOperatorOptions(FILTER_MAP_KEYS.ASSOCIATION)
});

export const buildConfigRefFilterSpec = ({ key, field, scalarName, collectionId, queryParams, nullable }) => ({
  key,
  field,
  label: capitalizeAndCleanUnderscore(key),
  isActive: false,
  activeSince: null,
  value: null,
  mapKey: FILTER_MAP_KEYS.CONFIG_REF,
  scalarName,
  collectionId,
  queryParams,
  nullable,
  operator: getDefaultOperator(FILTER_MAP_KEYS.CONFIG_REF),
  operatorOptions: buildOperatorOptions(FILTER_MAP_KEYS.CONFIG_REF)
});

// `field` is the dotted `<association>.<sub-field>` path (e.g. `locations.location_id`) —
// the backend resolves it to a column on the association table and filters via an EXISTS.
// The backend only exact-matches association columns, so sub-fields use IS/IS_NOT, never
// substring ops. `key` mirrors `field` and keeps each sub-field unique in the filter UI.
export const buildAssociationFilterSpecs = (field) => {
  if (!isAssociationField(field)) return [];
  const source = capitalizeAndCleanUnderscore(field.name);
  return getAssociationSubFields(field).reduce((specs, subField) => {
    const base = {
      key: `${field.name}.${subField.name}`,
      field: `${field.name}.${subField.name}`,
      isActive: false,
      activeSince: null,
      value: null
    };
    if (subField.entityType) {
      specs.push(buildEntityFilterSpec({
        key: base.key,
        field: base.field,
        entityType: subField.entityType,
        nullable: isNullableField(subField),
        label: `${capitalizeAndCleanUnderscore(subField.entityType)} (${source})`
      }));
      return specs;
    }
    const filterType = getColumnFilterType(subField);
    if (filterType) {
      specs.push({
        ...base,
        label: `${capitalizeAndCleanUnderscore(subField.name)} (${source})`,
        mapKey: filterType.mapKey,
        nullable: filterType.nullable,
        operator: EXACT_MATCH_OPERATORS[0],
        operatorOptions: EXACT_MATCH_OPERATORS.map((op) => ({ key: op, display_key: KNOWLEDGE_FILTER_OPERATORS[op].label }))
      });
    }
    return specs;
  }, []);
};
